import { z } from 'zod';

/** Opaque, stable, and never the platform's id for the channel (ADR 0008). */
export const ChannelId = z.string().regex(/^ch_[a-z0-9]{8,}$|^_platform$/).describe('Channel identifier');

/**
 * Cursor pagination only.
 *
 * No offset (Firestore cannot do it efficiently, and it is wrong under
 * concurrent writes) and no total count (it would invite a full-collection
 * scan on every page load).
 *
 * The cursor encodes a sort signature, so a cursor from a different ordering
 * is rejected rather than silently returning the wrong page.
 */
export const PageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
});

/** What a create answers with. One definition, so the client can reuse it. */
export const Created = z.object({ id: z.string() });

/** For endpoints whose only answer is "it worked". */
export const Ok = z.object({ ok: z.boolean() });

export function pageOf<T extends z.ZodType>(item: T) {
  return z.object({ data: z.array(item), next_cursor: z.string().nullable() });
}

/**
 * RFC 9457 problem+json. `code` is the contract, not `title` or `status`.
 *
 * The single definition: TomoError in ./errors.ts carries the codes, this
 * carries the wire shape, and the OpenAPI generator narrows `code` to the
 * enum of declared codes when it emits the component.
 */
export const Problem = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  code: z.string(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  request_id: z.string(),
  errors: z.array(z.object({ path: z.string(), code: z.string(), message: z.string() })).optional(),
});

export type Problem = z.infer<typeof Problem>;
